import { Button, Checkbox, Intent, Menu, MenuItem, Popover } from "@snpjs/core";
import { Download, FileCsv, MicrosoftExcelLogo } from "@snpjs/icons";
import { useState } from "react";

import { AdminViewApi } from "../../api/AdminViewApi";
import { Table2 } from "../../components/table2/Table2";
import { texts } from "../../i18n/texts";
import { useRequest } from "../../hooks/useRequest";
import useTranslation from "../../hooks/useTranslation";
import { Message } from "../../services/Message";
import { ILicenseLogEntry } from "../../shared/model/ILicenseLogEntry";
import UUID from "../../utils/UUID";
import { writeLocalStorage } from "../../utils/writeLocalStorage";
import { exportTableData } from "./utils/exportTableData";
import tableStyles from "./AdminViewSection.module.scss";
import styles from "./UpdateSyncSection.module.scss";

export const UpdateSyncSection: React.FC = () => {
  const { t } = useTranslation();
  const request = useRequest();
  const [projectId, setProjectId] = useState("");
  const [email, setEmail] = useState(false);
  const [simulationMode, setSimulationMode] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<ILicenseLogEntry[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isBehaviorExpanded, setIsBehaviorExpanded] = useState(true);

  const handleShowProducts = (products: ILicenseLogEntry[]) => {
    const storageKey = `swpo.project-analysis.products.${UUID.randomUUID()}`;
    writeLocalStorage(storageKey, products);
    const productsUrl = `${window.location.origin}/admin-view/project-analysis/products?key=${encodeURIComponent(
      storageKey
    )}`;
    window.open(productsUrl, "_blank", "noopener,noreferrer");
  };

  const exportData = (format: "csv" | "excel", rows: ILicenseLogEntry[] = results) => {
    exportTableData(rows, format, "update_sync_project_details", "ProjectDetails");
  };

  const exportMenu = (table?: any) => {
    const rows = table
      ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry)
      : results;

    return (
      <Menu>
        <MenuItem icon={<FileCsv />} text="CSV" onClick={() => exportData("csv", rows)} />
        <MenuItem icon={<MicrosoftExcelLogo />} text="Excel" onClick={() => exportData("excel", rows)} />
      </Menu>
    );
  };

  const handleRun = async () => {
    setIsLoading(true);
    await request.send(async () => {
      const result = await AdminViewApi.updateSync(projectId.trim() || undefined, {
        email,
        simulationMode,
        showResults,
      });
      if (result.successMessage) {
        Message.success(result.successMessage);
      }
      setResults(showResults ? result.projects : []);
      setHasLoaded(showResults);
    });
    setIsLoading(false);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.controlsArea}>
        <div className={styles.controls}>
          <div className={styles.formRow}>
            <label className={styles.projectLabel} htmlFor="update-sync-project-id">
              Project ID:
            </label>
            <input
              id="update-sync-project-id"
              className={styles.projectInput}
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
            />
            <Button
              intent="primary"
              text="Run"
              onClick={handleRun}
              loading={isLoading}
            />
          </div>

          <div className={styles.options}>
            <button
              className={styles.optionsHeader}
              type="button"
              aria-expanded={isBehaviorExpanded}
              aria-controls="update-sync-options"
              onClick={() => setIsBehaviorExpanded((expanded) => !expanded)}
            >
              <span className={styles.optionsHeaderText}>
                <strong>Run behavior</strong>
                <span>These settings control what happens when you select Run.</span>
              </span>
              <span className={styles.caret} aria-hidden="true" />
            </button>
            {isBehaviorExpanded && (
              <div id="update-sync-options">
                <label className={styles.checkboxLabel} htmlFor="update-sync-email">
              <Checkbox
                id="update-sync-email"
                checked={email}
                onChange={(event) => setEmail(event.target.checked)}
              />
              <span className={styles.optionContent}>
                <span className={styles.optionHeading}>
                  <span className={styles.optionTitle}>Email contract owner</span>
                  <span
                    className={`${styles.optionStatus} ${
                      email && !simulationMode ? styles.enabled : styles.disabled
                    }`}
                  >
                    {email && !simulationMode ? "ENABLED" : "DISABLED"}
                  </span>
                </span>
                <span className={styles.optionDescription}>
                  {email
                    ? simulationMode
                      ? t(texts.adminView.runBehavior.emailSimulationMode)
                      : t(texts.adminView.runBehavior.emailEnabled)
                    : t(texts.adminView.runBehavior.emailDisabled)}
                </span>
              </span>
                </label>
                <label
                  className={styles.checkboxLabel}
                  htmlFor="update-sync-simulation-mode"
                >
              <Checkbox
                id="update-sync-simulation-mode"
                checked={simulationMode}
                onChange={(event) => setSimulationMode(event.target.checked)}
              />
              <span className={styles.optionContent}>
                <span className={styles.optionHeading}>
                  <span className={styles.optionTitle}>Simulation Mode</span>
                  <span
                    className={`${styles.optionStatus} ${
                      simulationMode ? styles.enabled : styles.disabled
                    }`}
                  >
                    {simulationMode ? "ENABLED" : "DISABLED"}
                  </span>
                </span>
                <span className={styles.optionDescription}>
                  {simulationMode
                    ? t(texts.adminView.runBehavior.simulationModeEnabled)
                    : t(texts.adminView.runBehavior.simulationModeDisabled)}
                </span>
              </span>
                </label>
                <label className={styles.checkboxLabel} htmlFor="update-sync-show-results">
              <Checkbox
                id="update-sync-show-results"
                checked={showResults}
                onChange={(event) => setShowResults(event.target.checked)}
              />
              <span className={styles.optionContent}>
                <span className={styles.optionHeading}>
                  <span className={styles.optionTitle}>Show project details</span>
                  <span
                    className={`${styles.optionStatus} ${
                      showResults ? styles.enabled : styles.disabled
                    }`}
                  >
                    {showResults ? "ENABLED" : "DISABLED"}
                  </span>
                </span>
                <span className={styles.optionDescription}>
                  {showResults
                    ? t(texts.adminView.runBehavior.showResultsEnabled)
                    : t(texts.adminView.runBehavior.showResultsDisabled)}
                </span>
              </span>
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      {hasLoaded && (
        <div className={`${tableStyles.tableContainer} ${styles.resultsTable}`}>
          <Table2
            data={results}
            isLoading={isLoading}
            noResultsTitle="No results found"
            showUtilityToolbar={false}
            enableGlobalFilter={false}
            renderCell={(columnId, value) => {
              if (String(columnId).toUpperCase() !== "PRODUCTS") return undefined;

              let products: ILicenseLogEntry[] = [];
              if (Array.isArray(value)) {
                products = value as ILicenseLogEntry[];
              } else if (typeof value === "string") {
                try {
                  const parsed: unknown = JSON.parse(value);
                  products = Array.isArray(parsed) ? (parsed as ILicenseLogEntry[]) : [];
                } catch {
                  products = [];
                }
              }

              return (
                <Button
                  intent="primary"
                  text="Show Products"
                  onClick={() => handleShowProducts(products)}
                  disabled={products.length === 0}
                />
              );
            }}
            leftToolbar={(table) => (
              <Popover content={exportMenu(table)} placement="bottom-start" usePortal={false}>
                <Button
                  intent={Intent.PRIMARY}
                  icon={<Download />}
                  rightIcon="caret-down"
                  text="Export"
                  fill
                  alignText="left"
                />
              </Popover>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default UpdateSyncSection;